import { Button, Container, SelectChangeEvent, Stack } from '@mui/material';
import { useFormik } from 'formik';
import { FC, useState } from 'react';
import * as Yup from 'yup';
import {
    CustomTextField,
    generateTextFieldProps,
} from 'components/shared/form/custom-text-field';
import { EnumToDropdownOptions, generateRoutePath } from 'utils';
import { WithSpinner } from 'components/with-spinner';
import { useNavigate, useParams } from 'react-router';
import { RoutePaths } from 'routes';
import { AdminRoutePaths } from 'constants/enums/routes-constants';
import { PageTitle } from 'components/shared/page-title';
import { GuideForm } from './guide/guide-form';
import { GuideListTable } from './guide/guide-list-table';
import {
    CreateUpdateMaterialityQuestion,
    MaterialityQuestionAnswerValidation,
    MaterialityQuestionGuide,
} from 'api/hooks/materiality-question/materialityQuestionsTypes';
import { useCreateMaterialityQuestion } from 'api/hooks/materiality-question/useCreateMaterialityQuestion';
import { useUpdateMaterialityQuestion } from 'api/hooks/materiality-question/useUpdateMaterialityQuestion';
import { useGetMaterialityQuestion } from 'api/hooks/materiality-question/useGetMaterialityQuestion';
import {
    CustomSelectField,
    generateSelectFieldProps,
} from 'components/shared/form/custom-select-field';
import { getKeyByValue } from 'utils/enumHelper';

type Props = {};

export const AdminMaterialityQuestionForm: FC<Props> = () => {
    const navigate = useNavigate();
    const { materialityQuestionId } = useParams();
    const isNew = materialityQuestionId === '0';

    // const [scoresByAnswer, setScoresByAnswer] =
    //     useState<MaterialityQuestionScoreByAnswer[]>();
    const [guides, setGuides] = useState<MaterialityQuestionGuide[]>();
    const [showGuideForm, setShowGuideForm] = useState(false);

    const { data: materialityQuestion, isLoading: materialityQuestionLoading } =
        useGetMaterialityQuestion({
            id: materialityQuestionId ?? '',
            enabled: !isNew,
            onError: () => {
                navigateToMaterialityQuestionList();
            },
            onSuccess: (data) => {
                const answerValidation = getKeyByValue(
                    MaterialityQuestionAnswerValidation,
                    data?.answer_validation ?? ''
                );
                formik.setValues({
                    question_number: data?.question_number?.toString() ?? '',
                    question: data?.question,
                    max_score_possible:
                        data?.max_score_possible?.toString() ?? '',
                    scoring_guidance: data?.scoring_guidance,
                    answer_validation: answerValidation ?? '',
                });
                // setScoresByAnswer(data.scores_by_answer);
                setGuides(data.guides);
            },
        });

    const { mutate: createMaterialityQuestion } = useCreateMaterialityQuestion({
        onSuccess: () => navigateToMaterialityQuestionList(),
    });

    const { mutate: updateMaterialityQuestion } = useUpdateMaterialityQuestion({
        onSuccess: () => navigateToMaterialityQuestionList(),
    });

    const formName = AdminMaterialityQuestionForm.name;
    const validationSchema = Yup.object({
        question_number: Yup.string().required('Required'),
        question: Yup.string().required('Required'),
        max_score_possible: Yup.string().required('Required'),
    });
    const formik = useFormik({
        initialValues: {
            question_number: '',
            question: '',
            max_score_possible: '',
            scoring_guidance: '',
            answer_validation: '',
        },
        validationSchema,
        onSubmit: (data) => {
            const answerValidation =
                data.answer_validation as keyof typeof MaterialityQuestionAnswerValidation;

            const request: CreateUpdateMaterialityQuestion = {
                question: data.question,
                max_score_possible: parseFloat(data.max_score_possible),
                question_number: parseInt(data.question_number),
                scoring_guidance: data.scoring_guidance,
                answer_validation:
                    MaterialityQuestionAnswerValidation[answerValidation],
                guides: guides,
            };
            createUpdateMaterialityQuestion(request);
        },
    });
    type FormikTypes = typeof formik.initialValues;

    const navigateToMaterialityQuestionList = () => {
        navigate(
            generateRoutePath([
                RoutePaths.ADMIN,
                AdminRoutePaths.ADMIN_MATERIALITY_QUESTIONS,
            ])
        );
    };
    const handleDeleteGuide = (index: number) => {
        const newGuides = guides?.filter((value, i) => i !== index);

        setGuides(newGuides);
    };

    const handleAddGuide = (data: MaterialityQuestionGuide) => {
        const oldGuides = guides ?? [];
        const newGuides = [...oldGuides, data];

        setGuides(newGuides);
        setShowGuideForm(false);
    };

    const handleCancel = () => {
        navigateToMaterialityQuestionList();
    };

    const createUpdateMaterialityQuestion = (
        request: CreateUpdateMaterialityQuestion
    ) => {
        if (isNew) {
            createMaterialityQuestion(request);
        } else {
            updateMaterialityQuestion({
                id: materialityQuestion._id,
                request,
            });
        }
    };

    const handleSelect = (event: unknown | SelectChangeEvent) => {
        const { name, value } = (event as SelectChangeEvent).target;
        formik.setFieldValue(name, value);
    };

    const answerValidationOptions = EnumToDropdownOptions(
        MaterialityQuestionAnswerValidation
    );

    const isLoading = materialityQuestionLoading;

    return (
        <WithSpinner isLoading={isLoading}>
            <Container>
                <PageTitle
                    title={
                        isNew
                            ? 'Create New Materiality'
                            : `Update Materiality ${materialityQuestion?.question_number}`
                    }
                />
                <form onSubmit={formik.handleSubmit}>
                    <Stack spacing={2}>
                        <Stack direction={'row'} spacing={2}>
                            <CustomTextField
                                {...generateTextFieldProps<FormikTypes>(
                                    'question_number',
                                    'Question Number',
                                    formName,
                                    formik
                                )}
                            />
                            <CustomTextField
                                {...generateTextFieldProps<FormikTypes>(
                                    'max_score_possible',
                                    'Max Score Possible',
                                    formName,
                                    formik
                                )}
                            />
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <CustomTextField
                                {...generateTextFieldProps<FormikTypes>(
                                    'scoring_guidance',
                                    'Scoring Guidance',
                                    formName,
                                    formik
                                )}
                            />
                            <CustomTextField
                                {...generateTextFieldProps<FormikTypes>(
                                    'question',
                                    'Question',
                                    formName,
                                    formik
                                )}
                            />
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <CustomSelectField
                                options={answerValidationOptions}
                                onChange={handleSelect}
                                {...generateSelectFieldProps<FormikTypes>(
                                    'answer_validation',
                                    'Answer Validation',
                                    formName,
                                    formik
                                )}
                            />
                            <div style={{ width: '100%' }} />
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            {!showGuideForm ? (
                                <Button
                                    color='primary'
                                    role='submit'
                                    type='submit'
                                    variant='contained'
                                    onClick={() => setShowGuideForm(true)}
                                >
                                    Add New Guide
                                </Button>
                            ) : (
                                <GuideForm
                                    onAdd={handleAddGuide}
                                    onCancel={() => setShowGuideForm(false)}
                                />
                            )}
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <GuideListTable
                                data={guides}
                                onDelete={handleDeleteGuide}
                            />
                        </Stack>

                        <Stack
                            direction={'row'}
                            spacing={2}
                            justifyContent={'flex-end'}
                        >
                            <Button
                                color='primary'
                                data-testid={`${formName}-cancel`}
                                variant='contained'
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                color='primary'
                                data-testid={`${formName}-submit`}
                                role='submit'
                                type='submit'
                                variant='contained'
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Container>
        </WithSpinner>
    );
};
